import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ProductDetailsStyled } from "../../details/styled";
import {
  CustomBreadcrumb,
  PrimaryButton,
  CustomModal,
  CUSTOM_MODAL_TYPES,
} from "../../../../components";
import {
  MARKETPLACE_CONSTANTS,
  PRODUCT_HEADERS,
} from "../../../../config/constants";
import { Typography } from "@mui/material";
import { colors } from "../../../../config/colors";
import { TEXT_ALIGN_LEFT } from "../../../../config/styles";
import { Form, Row } from "react-bootstrap";
import { API_URL } from "../../../../utils/Constants";
import { ApiService } from "../../../../utils/ApiService";
import { ROUTES } from "../../../../config/routes";
import { getMerchantId } from "utils/Helper";

const CategoryDetails = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if edit mode from navigation state
  const isEditMode = location.state?.mode === "edit";
  const isNewCategory = categoryId === "new";

  const [labelEn, setLabelEn] = useState("");
  const [labelAr, setLabelAr] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [hasError, setHasError] = useState({});
  const [loader, setLoader] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [failureMsg, setFailureMsg] = useState("");
  const [isEditing, setIsEditing] = useState(isEditMode || isNewCategory);

  useEffect(() => {
    // if (!isNewCategory && categoryId) {
    //   getCategoryDetails();
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const getCategoryDetails = async () => {
    setLoader(true);
    try {
      const url = `${API_URL.PRODUCT_CATEGORY}/${categoryId}`;
      const response = await ApiService.getRequest(url, PRODUCT_HEADERS);
      if (response?.status?.code === "Y") {
        const category = response?.body;
        setLabelEn(category?.labelEn || "");
        setLabelAr(category?.labelAr || "");
        setCategoryName(category?.name || "");
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
    }
    setLoader(false);
  };

  const handleFieldChange = (fieldName, value) => {
    switch (fieldName) {
      case "labelEn":
        setLabelEn(value);
        setHasError({ ...hasError, labelEn: "" });
        break;
      case "labelAr":
        setLabelAr(value);
        setHasError({ ...hasError, labelAr: "" });
        break;
      default:
        break;
    }
  };

  const handleSave = async () => {
    // Validation
    if (!labelEn || !labelAr) {
      setHasError({
        labelEn: !labelEn ? "English label is required" : "",
        labelAr: !labelAr ? "Arabic label is required" : "",
      });
      return;
    }
    const merchantId = getMerchantId();
    setLoader(true);
    try {
      const requestData = {
        labelEn: labelEn,
        labelAr: labelAr,
        merchantId: merchantId,
      };

      const response = await ApiService.postRequest(
        API_URL.PRODUCT_CATEGORY,
        requestData,
        PRODUCT_HEADERS
      );

      if (response?.status?.code === "Y") {
        setHasError({});
        setSuccessMsg(
          response?.status?.message || "Category saved successfully"
        );
      } else if (response?.status?.code === "N") {
        setFailureMsg(response?.status?.message || "Failed to save category");
      }
    } catch (error) {
      console.error("Error saving category:", error);
      setFailureMsg("Failed to save category. Please try again.");
    }
    setLoader(false);
  };

  const pageTitle = isNewCategory
    ? "New Category"
    : isEditing
    ? `Edit: ${labelEn || categoryName}`
    : labelEn || categoryName;

  return (
    <ProductDetailsStyled>
      <CustomBreadcrumb
        isFromMarketplace={true}
        header={MARKETPLACE_CONSTANTS.CATEGORIES}
        currentPage={pageTitle}
      >
        <Form className="modal-form" autoComplete="nope">
          <div className="configuration-page">
            <div className="configuration-general">
              <Typography variant="h5" className="mb-4">
                Category Information
              </Typography>

              {/* Label English */}
              <Form.Floating as={Row} className="mb-1 mx-1" controlId="labelEn">
                <Form.Control
                  type="text"
                  placeholder="Label (English)"
                  value={labelEn}
                  onChange={(e) => handleFieldChange("labelEn", e.target.value)}
                  disabled={!isEditing}
                  required
                />
                <label htmlFor="labelEn">
                  <span>Label (English)</span>
                  <span style={{ color: colors.warning }}> *</span>
                </label>
              </Form.Floating>
              {hasError.labelEn ? (
                <div
                  className="mb-3 mx-1"
                  style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
                >
                  {hasError.labelEn}
                </div>
              ) : (
                <Typography
                  variant="subtitle2"
                  className="mx-1 mb-3"
                  sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
                >
                  Enter the category name in English
                </Typography>
              )}

              {/* Label Arabic */}
              <Form.Floating as={Row} className="mb-1 mx-1" controlId="labelAr">
                <Form.Control
                  type="text"
                  placeholder="Label (Arabic)"
                  value={labelAr}
                  onChange={(e) => handleFieldChange("labelAr", e.target.value)}
                  disabled={!isEditing}
                  required
                  dir="rtl"
                />
                <label htmlFor="labelAr">
                  <span>Label (Arabic)</span>
                  <span style={{ color: colors.warning }}> *</span>
                </label>
              </Form.Floating>
              {hasError.labelAr ? (
                <div
                  className="mb-3 mx-1"
                  style={{ color: colors.warning, textAlign: TEXT_ALIGN_LEFT }}
                >
                  {hasError.labelAr}
                </div>
              ) : (
                <Typography
                  variant="subtitle2"
                  className="mx-1 mb-3"
                  sx={{ color: colors.grayLight, textAlign: TEXT_ALIGN_LEFT }}
                >
                  Enter the category name in Arabic
                </Typography>
              )}
            </div>
          </div>

          {/* Action Buttons */}

          <div className="configuration-page p-0 border-0">
            <PrimaryButton
              label={
                isNewCategory ? "Create" : MARKETPLACE_CONSTANTS.SAVE_BUTTON
              }
              onClick={handleSave}
              disabled={loader}
            />
          </div>
        </Form>
      </CustomBreadcrumb>

      {/* Success/Failure Modal */}
      <CustomModal
        show={!!successMsg || !!failureMsg}
        modalType={
          failureMsg ? CUSTOM_MODAL_TYPES.FAILURE : CUSTOM_MODAL_TYPES.SUCCESS
        }
        onHide={() => {
          setSuccessMsg("");
          setFailureMsg("");
          if (successMsg) {
            navigate(ROUTES.CATEGORIES);
          }
        }}
        title={failureMsg || successMsg}
        message=""
      />
    </ProductDetailsStyled>
  );
};

export default CategoryDetails;
